import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { TableRow, TableCell, Typography } from '@mui/material';
import { Collector, CollectorSettingScope, Setting } from 'graphql/types';
import { useFeatureFlag } from 'modules/common/hooks';
import { ScopedCell } from './Cells/ScopedCell';
import { DisconnectCell } from './Cells/DisconnectCell';
import { StatusCell } from './Cells/StatusCell';

const useStyles = makeStyles((theme) => ({
  tableRow: {
    '& > td, & > th': {
      borderBottom: theme.spacing(0),
      padding: theme.spacing(0.5, 0)
    }
  },
  collectorName: {
    alignItems: 'center',
    display: 'inline-flex',
    lineHeight: theme.spacing(2)
  },
  img: {
    width: theme.spacing(4),
    marginRight: theme.spacing(1.5)
  },
  status: {
    '& > label': {
      marginRight: theme.spacing(0)
    }
  },
  button: {
    marginTop: 'auto',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(0.2),
    width: '100%',
    backgroundColor: theme.palette.primary.main
  }
}));
type UserPreferencesInfo = {
  installId: string;
  name: string;
  snackBarMessage: string | undefined;
};
type onOptOut = () => void;
type InstalledCollectorRowProps = {
  info: Collector | undefined;
  scope: CollectorSettingScope;
  installedDate: string;
  settings: Setting[];
  isAdmin: boolean;
  installId: string;
  optInStatus: boolean;
  setOpenConfirmDialog: (value: boolean) => void;
  setUserPreferencesInfo: (value: UserPreferencesInfo) => void;
  setOnOptOut: (value: onOptOut) => void;
  setOpenSnackBar: (value: boolean) => void;
  allowUserToOptOut: boolean;
};

export const InstalledCollectorRow: React.FC<InstalledCollectorRowProps> = ({
  info,
  scope,
  installedDate,
  settings,
  isAdmin,
  installId,
  optInStatus,
  setOpenConfirmDialog,
  setUserPreferencesInfo,
  setOnOptOut,
  setOpenSnackBar,
  allowUserToOptOut
}: InstalledCollectorRowProps) => {
  const classes = useStyles();
  const isTiwScreenReaderEnabled = useFeatureFlag('isTiwScreenReaderEnabled');
  return (
    <TableRow
      data-testid={'installed-collectors-row'}
      className={classes.tableRow}
    >
      <TableCell
        data-testid={`installed-collector-${scope}`}
        component={isTiwScreenReaderEnabled ? 'th' : undefined}
        scope={isTiwScreenReaderEnabled ? 'row' : undefined}
      >
        <Typography className={classes.collectorName}>
          <img
            src={info?.image?.src}
            alt={info?.name}
            className={classes.img}
            aria-hidden={true}
          />
          {info?.name}
        </Typography>
      </TableCell>
      <ScopedCell
        scope={scope}
        userText={'collectorType.user'}
        tenantText={'collectorType.company'}
      />
      <ScopedCell
        scope={scope}
        userText={'installedBy.self'}
        tenantText={'installedBy.admin'}
      />
      <TableCell>
        <Typography>{installedDate ? `${installedDate}` : ''}</Typography>
      </TableCell>
      <StatusCell
        scope={scope}
        collectorName={info!.name}
        settings={settings}
        installId={installId}
        optInStatus={optInStatus}
        setOpenConfirmDialog={setOpenConfirmDialog}
        setUserPreferencesInfo={setUserPreferencesInfo}
        setOnOptOut={setOnOptOut}
        setOpenSnackBar={setOpenSnackBar}
        allowUserToOptOut={allowUserToOptOut}
      />
      <DisconnectCell
        scope={scope}
        info={info}
        isAdmin={isAdmin}
        installId={installId}
      />
    </TableRow>
  );
};

export default React.memo(InstalledCollectorRow);
